import React from "react";
import {
  Check,
  Signature,
  ReadCvLogo,
  UploadSimple,
  IdentificationBadge,
  PaperPlaneTilt,
  CalendarDots,
} from "@phosphor-icons/react";
import { UserPermissions } from "types/permissions";
import type { ClientTask } from "./index";
import {
  MarkTaskCompleteButton,
  ResendLoginButton,
  AssignClinicianButton,
  ScheduleCallButton,
  UploadTreatmentPlanButton,
  UpdateTreatmentPlanButton,
  MarkAssessmentCompleteButton,
} from "../TimelineStatistics/TaskButtons";

// ─── Domain types ────────────────────────────────────────────────────────────

interface TreatmentPlan {
  id: string | number;
  approved: boolean;
  filename: string;
  cognito_id: string;
  document_id?: string | number;
}

interface CustomerDetails {
  details?: { client_id: string | number };
  clinician?: { clinician_user_id: string | number };
  treatmentPlans: TreatmentPlan[];
}

interface CurrentUser {
  id: string | number;
}

// ─── Grouped prop interfaces (exported for the parent to build) ───────────────

export interface ClientContext {
  customerDetails?: CustomerDetails;
  userPermissions?: UserPermissions;
  currentUser?: CurrentUser;
  isDirector?: boolean;
  verified?: boolean;
  isActive?: boolean;
  isClinician?: boolean;
}

export interface TaskHandlers {
  onMarkClientTaskComplete: (params: {
    clientId: string | number | undefined;
    clientTaskId: string | number;
  }) => void;
  onResendLoginEmail?: () => void;
  onOpenAssignClinician?: (type: string, value: boolean) => void;
  onToggleScheduleClientVideo?: (reschedule: boolean) => void;
  onToggleAssessmentComplete?: () => void;
  onToggleTreatmentPlanDialog?: () => void;
  onOpenUpdateTreatmentPlan?: (plan: TreatmentPlan | undefined) => void;
  onApproveTreatmentPlan?: () => void;
}

export interface LoadingState {
  markTaskComplete?: boolean;
  treatmentPlanUploading?: boolean;
  treatmentPlanApproving?: boolean;
  resendingLoginEmail?: boolean;
}

// ─── Button config logic ─────────────────────────────────────────────────────

type ButtonType =
  | "mark_complete"
  | "customer_care_mark_complete"
  | "resend_login"
  | "assign_clinician"
  | "schedule_call"
  | "mark_assessment"
  | "upload_treatment_plan"
  | "update_treatment_plan"
  | "approve_treatment_plan"
  | "none";

interface TaskButtonConfig {
  showButton: boolean;
  buttonText: string;
  buttonType: ButtonType;
  icon?: React.ReactNode;
}

const markCompleteIcons: Record<string, React.ReactNode> = {
  pre_auth_signed: <Signature />,
  ongoing_auth_signed: <Signature />,
  treatment_plan_approved: <ReadCvLogo />,
};

const markCompleteLabels: Record<string, string> = {
  diagnosis_uploaded: "Mark Complete",
  pre_auth_signed: "Pre-Auth Signed",
  pre_auth_submitted: "Pre-Auth Submitted",
  treatment_plan_approved: "Approve Treatment Plan",
  ongoing_auth_signed: "Authorization Signed",
  ongoing_auth_submitted: "Authorization Submitted",
  treatment_plan_review_scheduled: "Mark Complete",
  treatment_plan_review_completed: "Mark Complete",
};

const automaticTasks = new Set([
  "client_invited",
  "orientation_complete",
  "pre_auth_received",
  "first_assessment_completed",
  "re_auth_initiated",
  "ongoing_auth_received",
  "first_therapy_call_completed",
]);

export const getTaskButtonConfig = (taskType: string, taskCompleted: boolean): TaskButtonConfig => {
  if (automaticTasks.has(taskType)) {
    return { showButton: false, buttonText: "", buttonType: "none" };
  }

  const specialCases: Record<string, TaskButtonConfig> = {
    client_logged_in: {
      showButton: true,
      buttonText: "Resend Login Email",
      buttonType: "resend_login",
      icon: <PaperPlaneTilt />,
    },
    clinician_assigned: {
      showButton: true,
      buttonText: "Assign Clinician",
      buttonType: "assign_clinician",
      icon: <IdentificationBadge />,
    },
    assessment_scheduled: {
      showButton: true,
      buttonText: "Schedule Appointment",
      buttonType: "schedule_call",
      icon: <CalendarDots />,
    },
    first_therapy_call_scheduled: {
      showButton: true,
      buttonText: "Schedule Appointment",
      buttonType: "schedule_call",
      icon: <CalendarDots />,
    },
    assessment_process_completed: {
      showButton: true,
      buttonText: "Mark Complete",
      buttonType: "mark_assessment",
      icon: <Check />,
    },
    treatment_plan_submitted: taskCompleted
      ? {
          showButton: true,
          buttonText: "Update Plan",
          buttonType: "update_treatment_plan",
          icon: <UploadSimple />,
        }
      : {
          showButton: true,
          buttonText: "Upload Plan",
          buttonType: "upload_treatment_plan",
          icon: <UploadSimple />,
        },
    treatment_plan_approved: {
      showButton: true,
      buttonText: "Approve Treatment Plan",
      buttonType: "approve_treatment_plan",
      icon: <ReadCvLogo />,
    },
    ongoing_auth_signed: {
      showButton: true,
      buttonText: "Authorization Signed",
      buttonType: "customer_care_mark_complete",
      icon: markCompleteIcons["ongoing_auth_signed"],
    },
    ongoing_auth_submitted: {
      showButton: true,
      buttonText: "Authorization Submitted",
      buttonType: "customer_care_mark_complete",
      icon: <Check />,
    },
  };

  if (specialCases[taskType]) return specialCases[taskType];

  const label = markCompleteLabels[taskType] ?? "Mark Complete";
  const icon = markCompleteIcons[taskType] ?? <Check />;
  return { showButton: true, buttonText: label, buttonType: "mark_complete", icon };
};

// ─── Config-map renderer ─────────────────────────────────────────────────────

type RenderCtx = {
  task: ClientTask;
  clientContext: ClientContext;
  handlers: TaskHandlers;
  loadingState: LoadingState;
  isPrimaryClinician: boolean;
  buttonText: string;
  icon?: React.ReactNode;
};

type ButtonRenderer = (ctx: RenderCtx) => React.ReactNode;

const buttonRenderers: Partial<Record<ButtonType, ButtonRenderer>> = {
  customer_care_mark_complete: ({
    task,
    clientContext,
    handlers,
    loadingState,
    buttonText,
    icon,
  }) => {
    if (!clientContext.userPermissions?.view_client_tasklist) return null;
    return (
      <MarkTaskCompleteButton
        buttonText={buttonText}
        startIcon={icon}
        clientTaskId={task.id}
        clientId={clientContext.customerDetails?.details?.client_id}
        markClientTaskComplete={() =>
          handlers.onMarkClientTaskComplete({
            clientId: clientContext.customerDetails?.details?.client_id,
            clientTaskId: task.id,
          })
        }
        loading={loadingState.markTaskComplete}
      />
    );
  },

  mark_complete: ({ task, clientContext, handlers, loadingState, buttonText, icon }) => (
    <MarkTaskCompleteButton
      buttonText={buttonText}
      startIcon={icon}
      clientTaskId={task.id}
      clientId={clientContext.customerDetails?.details?.client_id}
      markClientTaskComplete={({ clientTaskId }: { clientTaskId: string | number }) =>
        handlers.onMarkClientTaskComplete({
          clientId: clientContext.customerDetails?.details?.client_id,
          clientTaskId,
        })
      }
      loading={loadingState.markTaskComplete}
    />
  ),

  approve_treatment_plan: ({ clientContext, handlers, loadingState, buttonText, icon }) => {
    if (!clientContext.isDirector) return null;
    return (
      <MarkTaskCompleteButton
        buttonText={buttonText}
        startIcon={icon}
        markClientTaskComplete={() => handlers.onApproveTreatmentPlan?.()}
        loading={loadingState.treatmentPlanApproving}
      />
    );
  },

  resend_login: ({ clientContext, handlers, loadingState }) => {
    if (
      !clientContext.verified &&
      clientContext.isActive &&
      clientContext.userPermissions?.send_reset_password &&
      handlers.onResendLoginEmail
    ) {
      return (
        <ResendLoginButton
          onResendLoginEmail={handlers.onResendLoginEmail}
          resendingLoginEmail={loadingState.resendingLoginEmail}
        />
      );
    }
    return null;
  },

  assign_clinician: ({ clientContext, handlers }) => {
    if (clientContext.userPermissions?.assign_clinician && handlers.onOpenAssignClinician) {
      return (
        <AssignClinicianButton
          onToggleAssignClinician={() => handlers.onOpenAssignClinician!("primary", true)}
        />
      );
    }
    return null;
  },

  schedule_call: ({ clientContext, handlers }) => {
    if (clientContext.userPermissions?.schedule_call) {
      return (
        <ScheduleCallButton
          onToggleScheduleClientVideo={handlers.onToggleScheduleClientVideo ?? (() => {})}
        />
      );
    }
    return null;
  },

  mark_assessment: ({ clientContext, handlers }) => {
    if (
      clientContext.userPermissions?.mark_assessment_complete &&
      handlers.onToggleAssessmentComplete
    ) {
      return (
        <MarkAssessmentCompleteButton
          onToggleAssessmentComplete={handlers.onToggleAssessmentComplete}
        />
      );
    }
    return null;
  },

  upload_treatment_plan: ({ clientContext, handlers, loadingState, isPrimaryClinician }) => {
    if (
      clientContext.userPermissions?.upload_treatment_plan &&
      (isPrimaryClinician || clientContext.isDirector) &&
      handlers.onToggleTreatmentPlanDialog
    ) {
      return (
        <UploadTreatmentPlanButton
          onToggleTreatmentPlanDialog={handlers.onToggleTreatmentPlanDialog}
          loading={loadingState.treatmentPlanUploading}
        />
      );
    }
    return null;
  },

  update_treatment_plan: ({ clientContext, handlers, loadingState, isPrimaryClinician }) => {
    const unapproved = clientContext.customerDetails?.treatmentPlans?.find((p) => !p.approved);
    if (
      (isPrimaryClinician || clientContext.isDirector) &&
      handlers.onOpenUpdateTreatmentPlan &&
      unapproved
    ) {
      return (
        <UpdateTreatmentPlanButton
          customerDetails={clientContext.customerDetails}
          onOpenUpdateTreatmentPlan={handlers.onOpenUpdateTreatmentPlan}
          loading={loadingState.treatmentPlanUploading}
        />
      );
    }
    return null;
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

interface OnboardingActionButtonProps {
  task: ClientTask;
  clientContext: ClientContext;
  handlers: TaskHandlers;
  loadingState: LoadingState;
}

const OnboardingActionButton: React.FC<OnboardingActionButtonProps> = ({
  task,
  clientContext,
  handlers,
  loadingState,
}) => {
  const { showButton, buttonType, buttonText, icon } = getTaskButtonConfig(
    task.task,
    task.completed
  );

  if (!showButton || (task.completed && buttonType !== "update_treatment_plan")) return null;

  const isPrimaryClinician =
    String(clientContext.currentUser?.id) ===
    String(clientContext.customerDetails?.clinician?.clinician_user_id);

  const renderer = buttonRenderers[buttonType];
  return renderer ? (
    <>
      {renderer({
        task,
        clientContext,
        handlers,
        loadingState,
        isPrimaryClinician,
        buttonText,
        icon,
      })}
    </>
  ) : null;
};

// ─── Phase tracker drawer: step button config (label + onClick + loading) ───

export interface PhaseTrackerStepButton {
  label: string;
  onClick: () => void;
  loading?: boolean;
  icon?: React.ReactNode;
}

export function getPhaseTrackerStepButton(
  task: ClientTask,
  clientContext: ClientContext,
  handlers: TaskHandlers,
  loadingState: LoadingState
): PhaseTrackerStepButton | null {
  const config = getTaskButtonConfig(task.task, task.completed);
  if (!config.showButton || (task.completed && config.buttonType !== "update_treatment_plan"))
    return null;

  const isPrimaryClinician =
    String(clientContext.currentUser?.id) ===
    String(clientContext.customerDetails?.clinician?.clinician_user_id);
  const clientId = clientContext.customerDetails?.details?.client_id;

  switch (config.buttonType) {
    case "customer_care_mark_complete":
      if (!clientContext.userPermissions?.view_client_tasklist) return null;
    // falls through
    case "mark_complete":
      return {
        label: config.buttonText,
        icon: config.icon,
        onClick: () => handlers.onMarkClientTaskComplete?.({ clientId, clientTaskId: task.id }),
        loading: loadingState.markTaskComplete,
      };
    case "resend_login":
      if (
        !clientContext.verified &&
        clientContext.isActive &&
        clientContext.userPermissions?.send_reset_password &&
        handlers.onResendLoginEmail
      ) {
        return {
          label: config.buttonText,
          icon: config.icon,
          onClick: handlers.onResendLoginEmail,
        };
      }
      return null;
    case "assign_clinician":
      if (clientContext.userPermissions?.assign_clinician && handlers.onOpenAssignClinician) {
        return {
          label: config.buttonText,
          icon: config.icon,
          onClick: () => handlers.onOpenAssignClinician!("primary", true),
        };
      }
      return null;
    case "schedule_call": {
      if (clientContext.userPermissions?.schedule_call) {
        const scheduleOnClick: () => void = handlers.onToggleScheduleClientVideo
          ? () => handlers.onToggleScheduleClientVideo!(false)
          : () => {};
        return { label: config.buttonText, icon: config.icon, onClick: scheduleOnClick };
      }
      return null;
    }
    case "mark_assessment":
      if (
        clientContext.userPermissions?.mark_assessment_complete &&
        handlers.onToggleAssessmentComplete
      ) {
        return {
          label: config.buttonText,
          icon: config.icon,
          onClick: handlers.onToggleAssessmentComplete,
        };
      }
      return null;
    case "upload_treatment_plan":
      if (
        clientContext.userPermissions?.upload_treatment_plan &&
        (isPrimaryClinician || clientContext.isDirector) &&
        handlers.onToggleTreatmentPlanDialog
      ) {
        return {
          label: config.buttonText,
          icon: config.icon,
          onClick: handlers.onToggleTreatmentPlanDialog,
          loading: loadingState.treatmentPlanUploading,
        };
      }
      return null;
    case "approve_treatment_plan":
      if (!clientContext.isDirector) return null;
      return {
        label: config.buttonText,
        icon: config.icon,
        onClick: () => handlers.onApproveTreatmentPlan?.(),
        loading: loadingState.treatmentPlanApproving,
      };
    case "update_treatment_plan":
      if (
        clientContext.userPermissions?.upload_treatment_plan &&
        (isPrimaryClinician || clientContext.isDirector) &&
        handlers.onOpenUpdateTreatmentPlan &&
        clientContext.customerDetails?.treatmentPlans
      ) {
        const unapproved = clientContext.customerDetails.treatmentPlans.find(
          (p: TreatmentPlan) => !p.approved
        );
        if (!unapproved) return null;
        return {
          label: config.buttonText,
          icon: config.icon,
          onClick: () => handlers.onOpenUpdateTreatmentPlan!(unapproved),
          loading: loadingState.treatmentPlanUploading,
        };
      }
      return null;
    default:
      return null;
  }
}

export default OnboardingActionButton;
